import { z } from 'zod';

const PathsSchema = z.object({
  auth: z.object({
    signIn: z.string().min(1),
    signUp: z.string().min(1),
    verifyMfa: z.string().min(1),
    callback: z.string().min(1),
    passwordReset: z.string().min(1),
    passwordUpdate: z.string().min(1),
    termOfService: z.string().min(1),
  }),
  app: z.object({
    home: z.string().min(1),
    personalAccountSettings: z.string().min(1),
    personalProjects: z.string().min(1),
    personalProjectSettings: z.string().min(1),
    personalProjectPage: z.string().min(1),
    personalAccountBilling: z.string().min(1),
    personalAccountBillingReturn: z.string().min(1),
    personalCampaign: z.string().min(1),
    personalWorkflows: z.string().min(1),
    personalWorkflowsOverview: z.string().min(1),
    personalWorkflowsTicketChannels: z.string().min(1),
    personalWorkflowsQueue: z.string().min(1),
    personalWorkflowsAnalytics: z.string().min(1),
    accountHome: z.string().min(1),
    accountSettings: z.string().min(1),
    accountBilling: z.string().min(1),
    accountMembers: z.string().min(1),
    accountProjects: z.string().min(1),
    accountProjectPage: z.string().min(1),
    accountProjectSettings: z.string().min(1),
    accountBillingReturn: z.string().min(1),
    accountCampaign: z.string().min(1),
    accountWorkflows: z.string().min(1),
    accountWorkflowsOverview: z.string().min(1),
    accountWorkflowsTicketChannels: z.string().min(1),
    accountWorkflowsQueue: z.string().min(1),
    accountWorkflowsAnalytics: z.string().min(1),
    joinTeam: z.string().min(1),
    surveySettings: z.string().min(1),
    surveyGenerate: z.string().min(1),
  }),
});

const pathsConfig = PathsSchema.parse({
  auth: {
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    verifyMfa: '/auth/verify',
    callback: '/auth/callback',
    passwordReset: '/auth/password-reset',
    passwordUpdate: '/update-password',
    termOfService: '/terms-of-service',
  },
  app: {
    home: '/home',
    personalAccountSettings: '/home/settings',
    personalProjects: '/home/projects',
    personalProjectSettings: '/home/projects/[project]/settings',
    personalProjectPage: '/home/projects/[project]',
    personalAccountBilling: '/home/billing',
    personalAccountBillingReturn: '/home/billing/return',
    personalCampaign: '/home/campaigns',
    personalWorkflows: "/home/workflows",
    personalWorkflowsOverview: "/home/workflows/overview",
    personalWorkflowsTicketChannels: "/home/workflows/ticket-channels",
    personalWorkflowsQueue: "/home/workflows/queue",
    personalWorkflowsAnalytics: "/home/workflows/analytics",
    accountHome: '/home/[account]',
    accountSettings: `/home/[account]/settings`,
    accountBilling: `/home/[account]/billing`,
    accountMembers: `/home/[account]/members`,
    accountProjects: '/home/[account]/projects',
    accountProjectPage: '/home/[account]/projects/[project]',
    accountProjectSettings: '/home/[account]/projects/[project]/settings',
    accountBillingReturn: `/home/[account]/billing/return`,
    accountCampaign: '/home/[account]/campaigns',
    accountWorkflows: "/home/[account]/workflows",
    accountWorkflowsOverview: "/home/[account]/workflows/overview",
    accountWorkflowsTicketChannels: "/home/[account]/workflows/ticket-channels",
    accountWorkflowsQueue: "/home/[account]/workflows/queue",
    accountWorkflowsAnalytics: "/home/[account]/workflows/analytics",
    joinTeam: '/join',
    surveySettings: '/home/projects/[project]/surveys/[survey]/settings',
    surveyGenerate: '/home/surveys/generate',
  },
} satisfies z.infer<typeof PathsSchema>);

export default pathsConfig;

// Dynamic path builders

function buildAccountPath(account?: string) {
  return account ? `${account}/` : '';
}

export function getSurveyEditPath(surveyIdentifier: string, account?: string) {
  return `/home/${buildAccountPath(account)}surveys/${surveyIdentifier}/edit`;
}

export function getSurveyInsightHubPath(
  projectId: number,
  surveyIdentifier: string,
  account?: string,
) {
  return `/home/${buildAccountPath(account)}projects/${projectId}/surveys/${surveyIdentifier}/insight-hub`;
}

export function getSurveyGeneratePath(
  account?: string,
  params?: Record<string, string | number | undefined>,
) {
  const basePath = `/home/${buildAccountPath(account)}surveys/generate`;
  if (!params) return basePath;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (!value) continue;
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
}
